import React, { useEffect, useState } from 'react';

import { Box, Button, Dialog, Slide, ThemeProvider, Typography, Stack } from '@mui/material';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QueryClient, QueryClientProvider, QueryCache } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { MapProvider } from 'react-map-gl';
import { I18nextProvider, useTranslation } from 'react-i18next';

import './App.css';
import theme from './theme';
import i18n from './utils/i18n';
import MapZone from 'handimap-react-lib/containers/MapZone';
import { SettingLayers } from 'handimap-react-lib/containers/SettingLayers';
import { setOpenDrawer } from 'handimap-react-lib/store/actions/sideBarActions';
import { LayersEngine } from 'handimap-react-lib/components/LayersEngine';
import { MaptypeContainer } from 'handimap-react-lib/containers/MaptypeContainer';
import { callGetJson } from 'handimap-react-lib/api/jsonConfApi';
import { openModal } from 'handimap-react-lib/store/actions/modalActions';
import { baseMaps, extras } from './baseMap';
import { setLegendsPerma } from 'handimap-react-lib/store/actions/legendActions';
import { graphByProfile } from './utils/graphConverter';
import { ProfilChoices } from './components/ProfilChoices';
import { convertProfiltForApi } from './utils/profilConverter';


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: '300000',
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // 🎉 only show error toasts if we already have data in the cache
      // which indicates a failed background update
      if (query.state.data !== undefined) {
        console.info ('query failed', error);
      }
    },
  }),
});

const TransitionRight = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

export const LayerModal = ({open}) => {
  const {palette} = theme;
  const styles = {
    buttonSetting : {
      minHeight: 0, 
      minWidth: 0, 
      padding: 1, 
      borderRadius: '0.5em',
      fontSize: '1.7em',
      backgroundColor: palette.primary.contrastBackground,
      color: palette.primary.main,
      borderColor: palette.primary.main,
      fontStyle: 'normal',
      textTransform: 'none',
      '&:hover': {
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText
      }
    }
  };
  const dispatch = useDispatch();
  const {t} = useTranslation(); 
  return (
    <>
      <Box sx={{display: 'flex', position: 'absolute', zIndex: '100', bottom: null, top: '7.5em', right: 8 }}>
        <Button aria-label={t('layer.aria-open')} onClick={() => dispatch(setOpenDrawer(true))} sx={styles.buttonSetting} size='medium' variant='outlined'>
          <FontAwesomeIcon size='sm' icon={faLayerGroup} />
        </Button>
      </Box>
      <Dialog   
        fullScreen
        open={open}
        onClose={() => dispatch(setOpenDrawer(false))}
        TransitionComponent={TransitionRight}>
        <SettingLayers onClose={() => dispatch(setOpenDrawer(false))} isMobile = {true} convertProfiltForApi={convertProfiltForApi}/>
      </Dialog>
    </>
  );
};

function App() {

  const showDrawer = useSelector(state => state.sideBar.drawer);
  const profil = useSelector(state => state.profils.profil);
  const condition = useSelector(state => state.profils.condition);

  const [baseProfile, setBaseProfile] = useState(graphByProfile(profil));

  const { t } = useTranslation();
  const getLanguage = () => i18n.language || window.localStorage.i18nextLng;
  const dispatch = useDispatch();
  const {palette} = theme;

  const styles = {
    stackSide : {
      display: 'flex',
      width: '100%',
      minHeight: 'calc(100vh - 1em)',
    },
    sideBar: {
      boxShadow: '0px 5px 5px #888888',
      zIndex: null,
      alignContent: 'center', 
      maxWidth: '100%',
      minWidth: '100vw'
    },
    buttonMenu: {
      position: 'absolute', 
      top: 10, 
      left: 10, 
      zIndex: '100', 
      backgroundColor: palette.primary.main, 
      color: palette.primary.contrastText
    },
    map: {
      display: 'flex', 
      height: '100vh', 
      width: '100vw'
    },
    buttonSetting : {
      minHeight: 0, 
      minWidth: 0, 
      padding: 1, 
      borderRadius: '0.5em',
      fontSize: '0.875em',
      backgroundColor: palette.primary.contrastBackground,
      color: palette.primary.main,
      borderColor: palette.primary.main,
      fontStyle: 'normal',
      textTransform: 'none',
      '&:hover': {
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText
      }
    }
  };

  // handle url params to fill origin and destination inputs
  // origin=-3.380118759727253,47.75755855083423&destination=-3.365463687506235,47.748446408159424&profil=Déficience motrice&condition=Fauteuil roulant manuel&multimodal=false&selectedPath=1
  const queryParams = new URLSearchParams(window.location.search);
  const originFromUrl = queryParams.get('origin');
  const destinationFromUrl = queryParams.get('destination');
  const zoneFromUrl = queryParams.get('zone');
  useEffect(() => {
    if (zoneFromUrl !== null && zoneFromUrl !== undefined && zoneFromUrl !== '') {
      callGetJson('custom_zoom_zone_button.json').then(
        result => {
          const zoomByZone = Object.assign({}, ...result.map((x) => ({[x.zone]: x})));
          let zoneDes = zoomByZone[zoneFromUrl].textFrDesc;
          if (getLanguage()==='fr') {
            zoneDes = zoomByZone[zoneFromUrl].textFrDesc;
          } else if (getLanguage()==='en') {
            zoneDes = zoomByZone[zoneFromUrl].textEnDesc;
          } else if (getLanguage()==='es') {
            zoneDes = zoomByZone[zoneFromUrl].textEsDesc;
          }
          const zoneImage = zoomByZone[zoneFromUrl].image;
          const credits = zoomByZone[zoneFromUrl].credits;
          dispatch(openModal(zoneFromUrl, zoneDes, zoneImage, credits));
        }
      );
    } else {
      callGetJson('custom_info_city.json').then(
        result => {
          dispatch(openModal(`${t('common.welcome')} ${result.city}`, result['app-present']));
        }
      );
    }
    dispatch(setLegendsPerma(['acc', 'part_acc', 'not_acc', 'not_spec']));
  }, []);

  useEffect(() => {
    setBaseProfile(graphByProfile(profil));
  }, [profil]);

  return (
    <ThemeProvider theme={theme}>
      <I18nextProvider i18n={i18n}>
        <div className="App" style={{ display: 'flex'}}>
          <QueryClientProvider client={queryClient}>
            <MapProvider >
              <LayersEngine isMobile={true}/>
                  <>
                    <Box sx={{position: 'absolute', bottom: 75, left: 10, zIndex: 100}}>
                      <MaptypeContainer  />
                    </Box>
                    <div className="map-bar" aria-labelledby="Carte" style={styles.map}>
                      <MapZone baseMaps={baseMaps} extrasLayers={extras} baseGraph={baseProfile} originFromUrl={originFromUrl} destinationFromUrl={destinationFromUrl} zoneFromUrl={zoneFromUrl} isMobile={true} isInteractive={false} hiddenButtons={false} convertProfiltForApi={convertProfiltForApi}/>
                      <LayerModal open={showDrawer} isMobile={true}/>
                      <Stack direction="column" spacing={1} sx={{position: 'absolute', zIndex: '100', top: '0.5em', left: 10, maxWidth: '75%' }}>
                        <Typography variant="body1" style={{ backgroundColor: '#FFFFFFCC', padding: '0.5em 1.5em 0.5em 1.5em', borderRadius: '0.8em', alignSelf:'center', fontFamily: 'Luciole', fontSize: '0.8rem' }}>Pour plus d'informations sur l'accessibilité rendez-vous sur <a href={`https://www.herault-mobility.fr/handimap/?zone=${zoneFromUrl}`} target="_blank">herault-mobility.fr !</a></Typography>
                        <ProfilChoices />
                      </Stack>
                    </div>
                </>
            </MapProvider>
          </QueryClientProvider>
        </div>
      </I18nextProvider>
    </ThemeProvider>
  );
  
}

export default App;
